/**
 * The theatre map's layer set (design 358–366, and the canvas context menu at
 * 2522–2533).
 *
 * Five switches, in the design's own order and with its own labels. They are a
 * table rather than markup because two surfaces draw them — the LAYERS panel on
 * the canvas and the canvas right-click menu — and a list built twice is a list
 * that disagrees with itself the first time one of them changes.
 *
 * The keys are `region-map.js`'s: `api.setLayers(l)` merges this object into the
 * renderer's own state, so a renamed key here is a switch that silently stops
 * doing anything. That is what `MAP_LAYER_KEYS` is for — it is the list the
 * layer panel iterates *and* the list a test can hold against the vendored file.
 *
 * **Layers never move a marker.** The renderer solves each region's slots once,
 * over every unit that belongs there, visible or not — so what a layer decides
 * is what is *drawn on top of* the picture, never where the picture sits. That
 * is asserted in `test-dom/theatre-map.test.tsx` rather than trusted.
 */

export interface MapLayers {
  /** Zone fills, strokes and names. */
  readonly regions: boolean;
  /** The range-from-origin overlay for the selected unit. */
  readonly ranges: boolean;
  /** Contacts. Own assets are never hidden by a layer. */
  readonly hostile: boolean;
  /** The id beside each marker. */
  readonly labels: boolean;
  /** Weather hatching, and the mark beside each region name. */
  readonly weather: boolean;
}

export type MapLayerKey = keyof MapLayers;

/** Label order is the design's, top to bottom. */
export const MAP_LAYER_LABELS: readonly (readonly [MapLayerKey, string])[] = Object.freeze([
  ['regions', 'REGION ZONES'],
  ['ranges', 'REACH OVERLAY'],
  ['hostile', 'HOSTILE TRACKS'],
  ['labels', 'MARKER LABELS'],
  ['weather', 'WEATHER (WX)'],
] as const);

export const MAP_LAYER_KEYS: readonly MapLayerKey[] = Object.freeze(
  MAP_LAYER_LABELS.map(([key]) => key),
);

/**
 * The renderer's own defaults, restated so the console and the canvas start in
 * agreement.
 *
 * `region-map.js` initialises `state.layers` in `connectedCallback` and the
 * wrapper pushes this on mount; if the two disagreed, the panel would open
 * showing a switch in the wrong position and the first click would appear to do
 * nothing. Weather starts off because it hatches every zone it touches, and an
 * operator who has not asked for it is reading force disposition.
 */
export const DEFAULT_MAP_LAYERS: MapLayers = Object.freeze({
  regions: true,
  ranges: true,
  hostile: true,
  labels: true,
  weather: false,
});

// ---------------------------------------------------------------------------
// The orbital board's four (design 2522–2524, `spaceLayers`)
// ---------------------------------------------------------------------------

/**
 * A second table rather than a generalisation of the first, because the two
 * canvases genuinely draw different things.
 *
 * The design keys them separately for the same reason (`layers` versus
 * `spaceLayers`), and only one word overlaps in meaning: `hostile` and `labels`
 * are the same switch on both maps, `rings` is the orbital board's `regions`,
 * and `links` has no theatre equivalent at all. Merging them would give the SDA
 * panel a REACH OVERLAY switch that nothing reads and a WEATHER switch over a
 * canvas with no weather — a control that does nothing, which is the one thing
 * `model/context-menu.ts` refuses to render even disabled.
 *
 * The keys are `orbital-map.js`'s, exactly as the theatre's are `region-map.js`'s:
 * `api.setLayers(l)` merges this object into the renderer's own `state.layers`,
 * so a renamed key here is a switch that silently stops doing anything.
 */
export interface SpaceLayers {
  /** The four rings, their slot ticks and their regime labels. */
  readonly rings: boolean;
  /** Contacts. Own assets are never hidden by a layer. */
  readonly hostile: boolean;
  /** The id beside each marker. */
  readonly labels: boolean;
  /** The dashed line from the selected satellite to its TT&C station. */
  readonly links: boolean;
}

export type SpaceLayerKey = keyof SpaceLayers;

/** Label order is the design's, top to bottom. */
export const SPACE_LAYER_LABELS: readonly (readonly [SpaceLayerKey, string])[] = Object.freeze([
  ['rings', 'ORBITAL RINGS'],
  ['hostile', 'HOSTILE TRACKS'],
  ['labels', 'MARKER LABELS'],
  ['links', 'CROSS-DOMAIN LINKS'],
] as const);

export const SPACE_LAYER_KEYS: readonly SpaceLayerKey[] = Object.freeze(
  SPACE_LAYER_LABELS.map(([key]) => key),
);

/**
 * The renderer's own defaults (`orbital-map.js`, `connectedCallback`), restated
 * so the console and the canvas start in agreement.
 *
 * All four on, which is what the vendored element initialises `state.layers` to
 * — and unlike the theatre's weather switch there is nothing here an operator
 * has to ask for before the board answers its first question.
 */
export const DEFAULT_SPACE_LAYERS: SpaceLayers = Object.freeze({
  rings: true,
  hostile: true,
  labels: true,
  links: true,
});

// ---------------------------------------------------------------------------
// The cyber board's two (design 1943, 2523, `cyberLayers`)
// ---------------------------------------------------------------------------

/**
 * A **third** table, and the argument is the one the second table's header
 * already makes — held against its third caller, which is where M5-D3 stops
 * granting it for free.
 *
 * Two switches, against the theatre's five and the board's four. Nothing merges:
 * `labels` is the same switch on all three canvases, `hosts` is this board's
 * alone, and the theatre's `regions`/`ranges`/`weather` and the board's
 * `rings`/`links` have no cyber equivalent. A merged table would give the CYBER
 * panel a WEATHER switch over a canvas with no weather and an ORBITAL RINGS
 * switch over a canvas with no rings — a control that does nothing, which is the
 * one thing this console keeps refusing to draw even disabled.
 *
 * What *is* shared is `maps/LayerPanel.tsx`, and this is its third caller and
 * the first from outside the pair it was lifted for. It needed no change: the
 * component took its `[key, label]` table as a prop in Phase 6 and became
 * generic over the key, so a two-switch list was already expressible.
 *
 * The keys are `cyber-map.js`'s: `api.setLayers(l)` merges into the renderer's
 * own `state.layers`, and it merges *defensively* — `{hosts: l.hosts !== false,
 * labels: l.labels !== false}` — so an unrecognised key is silently dropped and
 * a renamed one here is a switch that turns its layer permanently **on**.
 */
export interface CyberLayers {
  /** The host grid hanging off each router, and the lines to it. */
  readonly hosts: boolean;
  /** The id under each card, and the gateway and segment titles. */
  readonly labels: boolean;
}

export type CyberLayerKey = keyof CyberLayers;

/** Label order is the design's, top to bottom. */
export const CYBER_LAYER_LABELS: readonly (readonly [CyberLayerKey, string])[] = Object.freeze([
  ['hosts', 'HOSTS'],
  ['labels', 'MARKER LABELS'],
] as const);

export const CYBER_LAYER_KEYS: readonly CyberLayerKey[] = Object.freeze(
  CYBER_LAYER_LABELS.map(([key]) => key),
);

/**
 * The renderer's own defaults (`cyber-map.js`, `connectedCallback`), restated so
 * the console and the canvas start in agreement.
 *
 * Both on. Turning `hosts` off leaves the enclaves and their gateways — the
 * topology without the estate — which is the one view this board has that the
 * rails cannot give.
 */
export const DEFAULT_CYBER_LAYERS: CyberLayers = Object.freeze({
  hosts: true,
  labels: true,
});
